<template>
  <v-flex>
    <v-layout row wrap>
      <!-- Email Accounts Section -->
      <v-flex xs12>
        <h2 class="primary--text pb-2">Setup Email</h2>
        <p v-if="account.emailAccounts">Configure email settings for your account.</p>
        <p v-else>No email setup is configured. Please set up email by clicking the button below.</p>
      </v-flex>

      <v-btn
        class="ml-0 mt-0"
        color="secondary"
        v-on:click="showCreateDialog = true">
        Setup New Email
      </v-btn>
      <v-flex xs12 px-0 pb-0 mb-0 mt-3>
        <EmailAccount 
          v-for="(emailAccount, index) in account.emailAccounts" 
          :key="emailAccount.id || index" 
          :emailAccount="emailAccount" 
          v-on:has-changed="hasChanged" 
          :index="index"
          :ref="`emailAccount-${emailAccount.id || index}`"
        ></EmailAccount>
      </v-flex>

      <SystemEmails v-if="isAccountAdmin"></SystemEmails>

      <!-- Response Templates Section -->
      <v-flex xs12 class="mt-5">
        <v-divider class="mb-4"></v-divider>
        <h2 class="primary--text pb-2">Response Templates</h2>
        <p>Manage reusable email response templates that can be quickly inserted when composing emails.</p>
        
        <v-layout row wrap class="mt-3">
          <v-flex xs12>
            <v-text-field
              v-model="searchQuery"
              label="Search response templates"
              prepend-icon="search"
              clearable
              class="mb-3"
            ></v-text-field>
          </v-flex>
        </v-layout>

        <v-layout
          v-if="filteredResponseTemplates.length > 0"
          mt-0 row wrap fluid
          class="script-card-container card-margin-fix">
          <ResponseTemplateCard
            class="mr-0"
            v-for="(template) in filteredResponseTemplates" 
            :key="`${template.caseTypeId}-${template.id}`"
            :template="template"
            v-on:removeTemplate="removeTemplate">
          </ResponseTemplateCard>
        </v-layout>
        
        <v-alert
          v-else-if="!loadingTemplates"
          type="info"
          :value="true"
          class="mt-3">
          <span v-if="searchQuery">No response templates found matching "{{ searchQuery }}"</span>
          <span v-else>No response templates found. Create response templates in your case type email templates section.</span>
        </v-alert>

        <v-progress-circular
          v-if="loadingTemplates"
          indeterminate
          color="primary"
          class="mt-3">
        </v-progress-circular>
      </v-flex>
    </v-layout>
    <CreateEmailAccountDialog 
      :isOpen="showCreateDialog" 
      @created="onEmailAccountCreated"
      @close="showCreateDialog = false"
    />
  </v-flex>
</template>

<script>
import EmailAccount from '@/components/accounts/account-config/EmailAccount.vue'
import CreateEmailAccountDialog from '@/components/accounts/account-config/CreateEmailAccountDialog.vue'
import ResponseTemplateCard from '@/components/accounts/account-config/ResponseTemplateCard.vue'
import SystemEmails from '@/components/accounts/account-config/SystemEmails.vue'
import { mapState } from 'vuex'

// account.emailAccounts
export default {
  name: "account-emails",
  components: { EmailAccount, CreateEmailAccountDialog, ResponseTemplateCard, SystemEmails },
  props: ["account"],
  data: () => ({
    changedEmailAccountIndeces: [],
    showCreateDialog: false,
    searchQuery: "",
    loadingTemplates: false
  }),
  computed: {
    ...mapState({
      caseTypes: state => state.caseTypes.all || [],
      isAccountAdmin: state => !!(state.currentUser && state.currentUser.isAccountAdmin)
    }),
    responseTemplates() {
      const templates = []
      if (this.caseTypes && this.caseTypes.length > 0) {
        this.caseTypes.forEach(caseType => {
          if (caseType.emailTemplates && caseType.emailTemplates.length > 0) {
            caseType.emailTemplates
              .filter(et => et.emailBodyTemplate === true)
              .forEach(template => {
                templates.push({
                  ...template,
                  caseTypeId: caseType.id,
                  caseTypeName: caseType.name,
                  caseTypeCode: caseType.code
                })
              })
          }
        })
      }
      return templates
    },
    filteredResponseTemplates() {
      if (!this.searchQuery) {
        return this.responseTemplates
      }
      const query = this.searchQuery.toLowerCase()
      return this.responseTemplates.filter(template => 
        template.name.toLowerCase().includes(query) ||
        (template.caseTypeName && template.caseTypeName.toLowerCase().includes(query)) ||
        (template.category && template.category.toLowerCase().includes(query))
      )
    }
  },
  mounted() {
    this.loadTemplates()
  },
  watch: {
    caseTypes: {
      handler() {
        this.loadTemplates()
      },
      deep: true
    }
  },
  methods: {
    async loadTemplates() {
      if (!this.caseTypes || this.caseTypes.length === 0) {
        this.loadingTemplates = true
        await this.$store.dispatch("reloadCaseTypes")
        this.loadingTemplates = false
      }
    },
    onEmailAccountCreated(emailAccount) {
      this.showCreateDialog = false
      // Open the newly created email account by finding it in the list and expanding it
      this.$nextTick(() => {
        // Wait for Vue to update the DOM with the new account
        setTimeout(() => {
          const index = this.account.emailAccounts.findIndex(ea => ea.id === emailAccount.id)
          if (index !== -1) {
            const refKey = `emailAccount-${emailAccount.id || index}`
            const refs = this.$refs[refKey]
            if (refs && refs.length > 0) {
              const component = refs[0]
              // Call the expand method if it exists
              if (component && typeof component.expand === 'function') {
                component.expand()
                // Scroll to the component
                if (component.$el) {
                  component.$el.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
                }
              }
            }
          }
        }, 100)
      })
    },
    hasChanged({ index }) {
      if (this.changedEmailAccountIndeces.indexOf(index) < 0) {
        this.changedEmailAccountIndeces.push(index)
      }
      this.$emit("update-account-data", { key: "emailAccountChangedIndeces", value: this.changedEmailAccountIndeces })
    },
    async removeTemplate(template) {
      await this.$store.dispatch("deleteEmailTemplate", { 
        emailTemplateId: template.id, 
        caseTypeId: template.caseTypeId 
      })
      await this.$store.dispatch("reloadCaseTypes")
    }
  }
}
</script>
